import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { Notice } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import { fetchTournaments } from "../lib/tournaments.js";
import { FranchiseManager } from "./Franchises.js";
import { PoolRows } from "./OrganiseRows.js";

/**
 * An auction tournament's teams, on a page of their own: the franchises, each one's captain in
 * every pool, and the purses. Reached from the tournament's Organise page, as an event's
 * Organise page is reached from there.
 *
 * Organisers only, and only for a tournament that picks its teams by auction. Both are
 * signposting; the database refuses the writes for everybody else regardless.
 */
export function TournamentTeams() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({ queryKey: ["tournaments"], queryFn: fetchTournaments });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  // Teams come from an auction; any other tournament has none to manage.
  if (!tournament.auction) {
    return <Navigate to={`/tournaments/${tournament.id}/manage`} replace />;
  }

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link
        to={`/tournaments/${tournament.id}/manage`}
        className="micro mb-4 inline-block underline"
      >
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow={`Organise · ${tournament.name}`} title="Teams" />
      <FranchiseManager tournament={tournament} />

      {/* Captains and purses are per pool, so they are on each pool's Squads page. */}
      <section className="mt-10 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
        <h2 className="micro mb-1">Captains and purses</h2>
        <PoolRows tournament={tournament} step="squads" />
      </section>
    </main>
  );
}
